import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Typography } from "@mui/material";
import {
  computeTimelineDatePreview,
  computeTimelineDateChange,
  type TimelineDatePreview,
  type TimelineDateChange,
  type TimelineInteractionMode,
} from "./timeline-drag-utils";
import {
  diffInCalendarDays,
  formatTimelineDateTime,
  parseTimelineDateTime,
  type TimelineDateValue,
} from "./timeline-date-utils";
import {
  type TimelineBarStyle,
  type TimelineItem,
  type TimelinePreviewLabels,
} from "./timeline-item.types";
import {
  getTimelineDatePosition,
  getTimelineStepWidth,
  isTimelineTimeZoom,
  type TimelineRange,
  type TimelineTimeScale,
  type TimelineZoom,
} from "./timeline-range";
import { TimelineDatePreviewTooltip } from "./TimelineDatePreviewTooltip";
import { MelaChip } from "@/ui/mela/MelaChip";
import { MelaTooltip } from "@/ui/mela/MelaTooltip";

interface TimelineBarProps {
  item: TimelineItem;
  left: number;
  width: number;
  range: TimelineRange;
  zoom: TimelineZoom;
  timeScale?: TimelineTimeScale;
  continuesBefore?: boolean;
  continuesAfter?: boolean;
  clipStartOffset?: number;
  clipEndOffset?: number;
  barStyle: TimelineBarStyle;
  ariaLabel: string;
  previewLabels: TimelinePreviewLabels;
  formatPreviewDuration: (preview: TimelineDatePreview) => string;
  hoverTooltipContent?: React.ReactNode;
  getScrollContainer: () => HTMLElement | null;
  pendingDateChange?: TimelineDateChange | null;
  onItemOpen?: (item: TimelineItem) => void;
  onDateChangeRequest: (change: TimelineDateChange) => void;
}

interface DragState {
  mode: TimelineInteractionMode;
  startClientX: number;
  startScrollLeft: number;
  clientX: number;
  clientY: number;
  stepDelta: number;
}

interface HoverState {
  clientX: number;
  clientY: number;
}

const RESIZE_HANDLE_WIDTH = 8;
const AUTOSCROLL_EDGE_SIZE = 56;
const AUTOSCROLL_MAX_SPEED = 22;
export const TIMELINE_BAR_HEIGHT = 34;
export const TIMELINE_BAR_TOP = `calc(50% - ${TIMELINE_BAR_HEIGHT / 2}px)`;

const getPreviewStyle = (
  mode: TimelineInteractionMode,
  left: number,
  width: number,
  pixelDelta: number,
  minimumWidth: number,
) => {
  if (mode === "move") {
    return {
      left: left + pixelDelta,
      width,
    };
  }

  if (mode === "resize-start") {
    const nextWidth = Math.max(minimumWidth, width - pixelDelta);
    return {
      left: left + (width - nextWidth),
      width: nextWidth,
    };
  }

  return {
    left,
    width: Math.max(minimumWidth, width + pixelDelta),
  };
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const getTimelineAutoScrollSpeed = (clientX: number, rect: DOMRect): number => {
  if (clientX < rect.left + AUTOSCROLL_EDGE_SIZE) {
    const distance = rect.left + AUTOSCROLL_EDGE_SIZE - clientX;

    return -clamp(
      Math.ceil((distance / AUTOSCROLL_EDGE_SIZE) * AUTOSCROLL_MAX_SPEED),
      1,
      AUTOSCROLL_MAX_SPEED,
    );
  }

  if (clientX > rect.right - AUTOSCROLL_EDGE_SIZE) {
    const distance = clientX - (rect.right - AUTOSCROLL_EDGE_SIZE);

    return clamp(
      Math.ceil((distance / AUTOSCROLL_EDGE_SIZE) * AUTOSCROLL_MAX_SPEED),
      1,
      AUTOSCROLL_MAX_SPEED,
    );
  }

  return 0;
};

const getDateDelta = (from: Date | null, to: Date | null): number | null => {
  if (!from || !to) {
    return null;
  }

  return diffInCalendarDays(from, to);
};

const getTimePositionStepDelta = (
  from: Date | null,
  to: Date | null,
  range: TimelineRange,
  zoom: TimelineZoom,
  stepWidth: number,
  timeScale?: TimelineTimeScale,
): number | null => {
  if (!from || !to || stepWidth <= 0) {
    return null;
  }

  return Math.round(
    (getTimelineDatePosition(to, range, zoom, timeScale) -
      getTimelineDatePosition(from, range, zoom, timeScale)) /
      stepWidth,
  );
};

const hasExplicitTime = (value: TimelineDateValue): boolean => {
  const date = parseTimelineDateTime(value);

  if (!date) {
    return false;
  }

  return (
    date.getHours() !== 0 ||
    date.getMinutes() !== 0 ||
    date.getSeconds() !== 0 ||
    date.getMilliseconds() !== 0
  );
};

const getExplicitDateTime = (value: TimelineDateValue): Date | null =>
  hasExplicitTime(value) ? parseTimelineDateTime(value) : null;

const getPendingDateChangeDayDelta = (
  change: TimelineDateChange | null | undefined,
  zoom: TimelineZoom,
  range: TimelineRange,
  stepWidth: number,
  timeScale?: TimelineTimeScale,
): number | null => {
  if (!change) {
    return null;
  }

  const getDelta = isTimelineTimeZoom(zoom)
    ? (from: Date | null, to: Date | null) =>
        getTimePositionStepDelta(from, to, range, zoom, stepWidth, timeScale)
    : getDateDelta;

  if (change.mode === "move") {
    return (
      getDelta(change.previousStartDate, change.nextStartDate) ??
      getDelta(change.previousDueDate, change.nextDueDate)
    );
  }

  if (change.mode === "resize-start") {
    return (
      getDelta(change.previousStartDate, change.nextStartDate) ??
      getDelta(change.previousDueDate, change.nextStartDate)
    );
  }

  if (change.mode === "resize-end") {
    return (
      getDelta(change.previousDueDate, change.nextDueDate) ??
      getDelta(change.previousStartDate, change.nextDueDate)
    );
  }

  return null;
};

export const TimelineBar = React.memo(function TimelineBar({
  item,
  left,
  width,
  range,
  zoom,
  timeScale,
  continuesBefore = false,
  continuesAfter = false,
  clipStartOffset = 0,
  clipEndOffset = 0,
  barStyle,
  ariaLabel,
  previewLabels,
  formatPreviewDuration,
  hoverTooltipContent,
  getScrollContainer,
  pendingDateChange,
  onItemOpen,
  onDateChangeRequest,
}: TimelineBarProps) {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [hoverState, setHoverState] = useState<HoverState | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const didRequestDateChangeRef = useRef(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const autoScrollFrameRef = useRef<number | null>(null);
  const stepWidth = getTimelineStepWidth(zoom);
  const isDragging = Boolean(dragState);

  const updateDragState = useCallback(
    (clientX: number, clientY: number) => {
      const currentDragState = dragStateRef.current;

      if (!currentDragState) {
        return;
      }

      const scrollDelta =
        (scrollContainerRef.current?.scrollLeft ??
          currentDragState.startScrollLeft) - currentDragState.startScrollLeft;
      const stepDelta = Math.round(
        (clientX - currentDragState.startClientX + scrollDelta) / stepWidth,
      );
      const nextDragState = {
        ...currentDragState,
        clientX,
        clientY,
        stepDelta,
      };

      dragStateRef.current = nextDragState;
      setDragState(nextDragState);
    },
    [stepWidth],
  );

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, mode: TimelineInteractionMode) => {
      event.preventDefault();
      event.stopPropagation();
      setHoverState(null);
      const scrollContainer = getScrollContainer();
      const nextDragState = {
        mode,
        startClientX: event.clientX,
        startScrollLeft: scrollContainer?.scrollLeft ?? 0,
        clientX: event.clientX,
        clientY: event.clientY,
        stepDelta: 0,
      };
      scrollContainerRef.current = scrollContainer;
      dragStateRef.current = nextDragState;
      setDragState(nextDragState);
    },
    [getScrollContainer],
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMove = (event: PointerEvent) => {
      updateDragState(event.clientX, event.clientY);
    };

    const handleUp = () => {
      const currentDragState = dragStateRef.current;
      dragStateRef.current = null;
      scrollContainerRef.current = null;
      setDragState(null);

      if (!currentDragState) {
        return;
      }

      const change = computeTimelineDateChange(
        item,
        currentDragState.mode,
        currentDragState.stepDelta,
        zoom,
        timeScale,
      );

      if (change) {
        didRequestDateChangeRef.current = true;
        onDateChangeRequest(change);
      }
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp, { once: true });

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [isDragging, item, onDateChangeRequest, timeScale, updateDragState, zoom]);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    const handleAutoScroll = () => {
      const currentDragState = dragStateRef.current;

      if (!currentDragState) {
        return;
      }

      const rect = scrollContainer.getBoundingClientRect();
      const scrollSpeed = getTimelineAutoScrollSpeed(
        currentDragState.clientX,
        rect,
      );

      if (scrollSpeed !== 0) {
        const maxScrollLeft =
          scrollContainer.scrollWidth - scrollContainer.clientWidth;
        const nextScrollLeft = clamp(
          scrollContainer.scrollLeft + scrollSpeed,
          0,
          maxScrollLeft,
        );

        if (nextScrollLeft !== scrollContainer.scrollLeft) {
          scrollContainer.scrollLeft = nextScrollLeft;
          updateDragState(currentDragState.clientX, currentDragState.clientY);
        }
      }

      autoScrollFrameRef.current =
        window.requestAnimationFrame(handleAutoScroll);
    };

    autoScrollFrameRef.current = window.requestAnimationFrame(handleAutoScroll);

    return () => {
      if (autoScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
      }
    };
  }, [isDragging, updateDragState]);

  const pendingDayDelta = useMemo(
    () =>
      getPendingDateChangeDayDelta(
        pendingDateChange,
        zoom,
        range,
        stepWidth,
        timeScale,
      ),
    [pendingDateChange, range, stepWidth, timeScale, zoom],
  );
  const hasPendingPreview = Boolean(
    !dragState && pendingDateChange && pendingDayDelta !== null,
  );
  const preview = useMemo(() => {
    const previewMode = dragState?.mode ?? pendingDateChange?.mode;
    const previewDayDelta = dragState?.stepDelta ?? pendingDayDelta;

    if (!previewMode || previewDayDelta === null) {
      return { left, width };
    }

    return getPreviewStyle(
      previewMode,
      left,
      width,
      previewDayDelta * stepWidth,
      stepWidth,
    );
  }, [
    dragState,
    left,
    pendingDateChange?.mode,
    pendingDayDelta,
    stepWidth,
    width,
  ]);
  const dragPreview = useMemo(() => {
    if (!dragState) {
      return null;
    }

    return computeTimelineDatePreview(
      item,
      dragState.mode,
      dragState.stepDelta,
      zoom,
      timeScale,
    );
  }, [dragState, item, timeScale, zoom]);
  const hoverPreview = useMemo(
    () => computeTimelineDatePreview(item, "move", 0, zoom, timeScale),
    [item, timeScale, zoom],
  );
  const activeDatePreview = dragPreview ?? (hoverState ? hoverPreview : null);
  const activePreviewPosition = dragState ?? hoverState;
  const formatPreviewDate = formatTimelineDateTime;
  const tooltipPreview = useMemo(() => {
    if (!activeDatePreview) {
      return null;
    }

    if (!dragState && hoverState) {
      return {
        ...activeDatePreview,
        nextStartDate:
          getExplicitDateTime(item.startDate) ??
          activeDatePreview.nextStartDate,
        nextDueDate:
          getExplicitDateTime(item.dueDate) ?? activeDatePreview.nextDueDate,
      };
    }

    return activeDatePreview;
  }, [
    activeDatePreview,
    dragState,
    hoverState,
    item.dueDate,
    item.startDate,
    zoom,
  ]);

  const updateHoverState = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType !== "mouse" || dragStateRef.current) {
        return;
      }

      setHoverState({
        clientX: event.clientX,
        clientY: event.clientY,
      });
    },
    [],
  );

  const handleOpen = useCallback(() => {
    if (!onItemOpen) {
      return;
    }

    onItemOpen(item);
  }, [item, onItemOpen]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (didRequestDateChangeRef.current) {
        didRequestDateChangeRef.current = false;
        return;
      }

      handleOpen();
    },
    [handleOpen],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handleOpen();
    },
    [handleOpen],
  );

  return (
    <>
      {isDragging || hasPendingPreview ? (
        <Box
          data-testid="timeline-bar-original-ghost"
          sx={{
            position: "absolute",
            top: TIMELINE_BAR_TOP,
            left,
            width,
            minWidth: 16,
            height: TIMELINE_BAR_HEIGHT,
            borderRadius: 1,
            border: `1px dashed ${barStyle.borderColor}`,
            background: barStyle.background,
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.12)",
            opacity: 0.38,
            pointerEvents: "none",
            zIndex: 3,
          }}
        />
      ) : null}

      <Box
        sx={{
          position: "absolute",
          top: TIMELINE_BAR_TOP,
          left: preview.left,
          width: preview.width,
          minWidth: 16,
          height: TIMELINE_BAR_HEIGHT,
          zIndex: isDragging || hasPendingPreview ? 20 : 2,
        }}
      >
        <MelaTooltip
          content={hoverTooltipContent}
          disabled={Boolean(dragState)}
          anchorToCursorOnOpen
          fullWidth
          interactive
          maxWidth={420}
          openDelay={500}
          tone="light"
        >
          <Box
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            onPointerDown={(event) => startDrag(event, "move")}
            onPointerEnter={updateHoverState}
            onPointerMove={updateHoverState}
            onPointerLeave={() => setHoverState(null)}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            sx={{
              width: "100%",
              height: TIMELINE_BAR_HEIGHT,
              borderRadius: 1,
              border: `1px solid ${barStyle.borderColor}`,
              background: barStyle.background,
              color: barStyle.color,
              cursor: dragState ? "grabbing" : "grab",
              boxShadow: dragState
                ? "0 4px 12px rgba(15, 23, 42, 0.2)"
                : "0 1px 3px rgba(15, 23, 42, 0.18)",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              position: "relative",
              userSelect: "none",
              touchAction: "none",
              transition: dragState ? "none" : "box-shadow 120ms ease",
              zIndex: 2,
            }}
          >
            {continuesBefore ? (
              <Box
                component="span"
                data-testid="timeline-bar-continues-before"
                sx={{
                  position: "absolute",
                  top: 3,
                  left: clipStartOffset + 1,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: "#facc15",
                  pointerEvents: "none",
                  zIndex: 6,
                }}
              />
            ) : null}
            {continuesAfter ? (
              <Box
                component="span"
                data-testid="timeline-bar-continues-after"
                sx={{
                  position: "absolute",
                  top: 3,
                  right: clipEndOffset + 1,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: "#facc15",
                  pointerEvents: "none",
                  zIndex: 6,
                }}
              />
            ) : null}
            <Box
              onPointerDown={(event) => startDrag(event, "resize-start")}
              sx={{
                width: RESIZE_HANDLE_WIDTH,
                alignSelf: "stretch",
                cursor: "ew-resize",
                flex: `0 0 ${RESIZE_HANDLE_WIDTH}px`,
                backgroundColor: "rgba(255,255,255,0.28)",
                position: "relative",
                zIndex: 2,
              }}
            />
            {item.leadingContent ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  minWidth: 0,
                  flex: "0 1 auto",
                  pl: 0.5,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {item.leadingContent}
              </Box>
            ) : null}
            {item.hideTitle ? null : item.titleChip ? (
              <MelaChip
                size="sm"
                tone="neutral"
                truncate
                variant="soft"
                style={{
                  flex: "0 1 auto",
                  margin: "0 6px",
                  minHeight: 18,
                  padding: "0 6px",
                  fontSize: "0.625rem",
                }}
              >
                {item.label || "-"}
              </MelaChip>
            ) : (
              <Typography
                component="span"
                sx={{
                  fontSize: 14,
                  lineHeight: "16px",
                  fontWeight: 600,
                  px: 0.75,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textShadow:
                    barStyle.textShadow ?? "0 1px 2px rgba(15, 23, 42, 0.45)",
                  whiteSpace: "nowrap",
                  flex: "1 1 auto",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {item.label || "-"}
              </Typography>
            )}
            <Box
              onPointerDown={(event) => startDrag(event, "resize-end")}
              sx={{
                width: RESIZE_HANDLE_WIDTH,
                marginLeft: "auto",
                alignSelf: "stretch",
                cursor: "ew-resize",
                flex: `0 0 ${RESIZE_HANDLE_WIDTH}px`,
                backgroundColor: "rgba(255,255,255,0.28)",
                position: "relative",
                zIndex: 2,
              }}
            />
          </Box>
        </MelaTooltip>
        {tooltipPreview &&
        activePreviewPosition &&
        (dragState || !hoverTooltipContent) ? (
          <TimelineDatePreviewTooltip
            clientX={activePreviewPosition.clientX}
            clientY={activePreviewPosition.clientY}
            items={[
              {
                label: previewLabels.start,
                value: formatPreviewDate(tooltipPreview.nextStartDate),
              },
              {
                label: previewLabels.end,
                value: formatPreviewDate(tooltipPreview.nextDueDate),
              },
              {
                label: previewLabels.duration,
                value: formatPreviewDuration(tooltipPreview),
              },
            ]}
          />
        ) : null}
      </Box>
    </>
  );
});
